import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  forwardRef,
  Input,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { NetworkApiService } from '@fleet/api/network/network-api.service';
import { AuthService } from '@fleet/auth/services/auth.service';
import { ApiResponse, NetworkServiceSearchResultModel } from '@fleet/model';

/**
 * Network service CVA.
 *
 * Template options are always `networkServiceId` (SELECT and LIST).
 *
 * writeValue:
 * - string → that id (or `[id]` when multiSelector)
 * - string[] → those ids
 * - object / object[] → extract `networkServiceId`
 *
 * onChange:
 * - `mode='id'` (itinerary, org job setting, position listener): forwards the inner id / id[]
 * - `mode='object'` (vehicle search multi, notification filter list): maps ids → objects
 *
 * Services load asynchronously; the last writeValue is re-applied after search
 * (`emitEvent: false`) so a patch before the list arrives still displays.
 */
@Component({
    selector: 'fleet-network-service-selector',
    templateUrl: './network-service-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NetworkServiceSelectorComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class NetworkServiceSelectorComponent implements OnInit {
  @Input() multiSelector = false;

  @Input() placeholder: string;
  @Input() matLabelClasses = false;
  @Input() classes: string;
  @Input() label: string;
  @Input() labelClass: string;
  @Input() prefixIcon = 'search';
  @Input() viewMode: 'SELECT' | 'LIST' = 'SELECT';
  control = new UntypedFormControl();
  @Input() mode = 'id';
  excludeIds = input<string[]>([]);
  excludeStringLabels = input<string[]>([]);
  required = input(false);
  networkServices: NetworkServiceSearchResultModel[] = [];
  private allServices = signal<NetworkServiceSearchResultModel[]>([]);
  private lastWritten: unknown;
  private hasWritten = false;
  filteredNetworkServices = computed(() => {
    const exclude = new Set(this.excludeIds() || []);
    const excludeLabels = new Set(
      (this.excludeStringLabels() || [])
        .map((label) => label?.trim())
        .filter((label): label is string => !!label),
    );
    return this.allServices().filter(
      (service) =>
        !exclude.has(service.networkServiceId) &&
        !excludeLabels.has((service.displayName || '').trim()),
    );
  });

  constructor(
    private networkApiService: NetworkApiService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    effect(() => {
      this.control.setValidators(this.required() ? Validators.required : null);
      this.control.updateValueAndValidity({ emitEvent: false });
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnInit(): void {
    this.networkApiService
      .searchNetworkServices(this.authService.networkId, {})
      .subscribe({
        next: (
          resp: HttpResponse<ApiResponse<NetworkServiceSearchResultModel[]>>
        ) => {
          this.networkServices = resp.body.data;
          this.allServices.set(resp.body.data || []);
          if (this.hasWritten) {
            this.applyValue(this.lastWritten);
          }
          this.changeDetectorRef.markForCheck();
        },
      });

    this.control.valueChanges.subscribe((value: any) => {
      if (value) {
        if (this.mode === 'id') {
          this.onChange(value);
        } else {
          //object
          const networkServices = value.map((networkServiceId: string) =>
            this.networkServices.find(
              (s) => s.networkServiceId === networkServiceId
            )
          );

          this.onChange(networkServices);
        }
      } else {
        this.onChange(null);
      }
      this.onTouched();
    });
  }

  onChange: any = (group: any) => {};
  onTouched: any = () => {};
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    this.hasWritten = true;
    this.lastWritten = value;
    this.applyValue(value);
  }

  private applyValue(value: unknown): void {
    if (value) {
      if (typeof value === 'string') {
        this.control.setValue(this.multiSelector ? [value] : value, {
          emitEvent: false,
        });
      } else if (Array.isArray(value)) {
        if (value.every((item) => typeof item === 'string')) {
          this.control.setValue(value, { emitEvent: false });
        } else {
          this.control.setValue(
            value.map(
              (networkService: NetworkServiceSearchResultModel) =>
                networkService.networkServiceId
            ),
            { emitEvent: false }
          );
        }
      } else {
        this.control.setValue(
          (value as NetworkServiceSearchResultModel).networkServiceId,
          { emitEvent: false }
        );
      }
    } else {
      this.control.setValue(null, { emitEvent: false });
    }
  }
}
